import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { eventsService } from '../services/eventsService';
import { EventFilters, EventTable, EventPagination, LoadingSpinner, Event } from '../components/events';
import { toISTISOString } from '../lib/utils';

const Transactions: React.FC = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);

    // Filter states
    const [dateRange, setDateRange] = useState<'1h' | '24h' | '7d' | '30d' | 'custom'>('custom');
    const [eventNameFilter, setEventNameFilter] = useState<string[]>(['EKYCDataStored', 'EKYCDataUpdated', 'JobCardDataStored', 'JobCardDataUpdated', 'MateAddressSet', 'PeriodAttendanceFinalized']);
    const [stateId, setStateId] = useState<number | null>(null);
    
    // Set default dates to 1 week ago to today
    const getDefaultDates = () => {
        const today = new Date();
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(today.getDate() - 7);
        return {
            from: oneWeekAgo,
            to: today
        };
    };

    const defaultDates = getDefaultDates();
    const [customFromDate, setCustomFromDate] = useState<string>(() => {
        return toISTISOString(defaultDates.from);
    });
    const [customToDate, setCustomToDate] = useState<string>(() => {
        return toISTISOString(defaultDates.to);
    });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEvents, setTotalEvents] = useState(0);

    // Fetch events with filters and pagination
    // Use JSON.stringify for array comparison to prevent unnecessary re-renders
    const eventNameFilterKey = JSON.stringify(eventNameFilter);

    useEffect(() => {
        const fetchEvents = async () => {
            // If no events are selected, show empty results
            if (eventNameFilter.length === 0) {
                setLoading(false);
                setEvents([]);
                setTotalEvents(0);
                setTotalPages(0);
                return;
            }

            try {
                setLoading(true);

                const response = await eventsService.searchTransactions({
                    page: currentPage,
                    pageSize: itemsPerPage,
                    filterType: 'custom',
                    eventNames: eventNameFilter,
                    ...(customFromDate && customToDate ? { fromDate: customFromDate, toDate: customToDate } : {}),
                    ...(stateId ? { stateId } : {}),
                });

                setEvents(response.events);
                setTotalEvents(response.totalCount);
                setTotalPages(response.totalPages);
            } catch (error) {
                console.error('Error fetching events:', error);
                setEvents([]);
                setTotalEvents(0);
                setTotalPages(0);
            } finally {
                setLoading(false);
                setIsApplyingFilters(false);
            }
        };

        fetchEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, dateRange, eventNameFilterKey, itemsPerPage, customFromDate, customToDate, stateId]);

    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    const handleCustomDateChange = (fromDate: string, toDate: string) => {
        setCustomFromDate(fromDate);
        setCustomToDate(toDate);
        setCurrentPage(1); // Reset to first page when changing date range
    };

    const [isApplyingFilters, setIsApplyingFilters] = useState(false);

    const handleApplyFilters = async () => {
        setCurrentPage(1); // Reset to first page when applying filters
        setExpandedRows(new Set()); // Clear expanded rows
        setIsApplyingFilters(true);
        
        // Force a refresh by directly calling the API
        try {
            if (eventNameFilter.length === 0) {
                setLoading(false);
                setEvents([]);
                setTotalEvents(0);
                setTotalPages(0);
                setIsApplyingFilters(false);
                return;
            }

            setLoading(true);

            const response = await eventsService.searchTransactions({
                page: 1,
                pageSize: itemsPerPage,
                filterType: 'custom',
                eventNames: eventNameFilter,
                ...(customFromDate && customToDate ? { fromDate: customFromDate, toDate: customToDate } : {}),
                ...(stateId ? { stateId } : {}),
            });

            setEvents(response.events);
            setTotalEvents(response.totalCount);
            setTotalPages(response.totalPages);
        } catch (error) {
            console.error('Error fetching events:', error);
            setEvents([]);
            setTotalEvents(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
            setIsApplyingFilters(false);
        }
    };

    const handleStateChange = (newStateId: number | null) => {
        setStateId(newStateId);
        setCurrentPage(1); // Reset to first page when changing state
    };

    const handleClearFilters = () => {
        setCurrentPage(1); // Reset to first page when clearing filters
        setExpandedRows(new Set()); // Clear expanded rows
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
        setExpandedRows(new Set());
    };

    const toggleRowExpansion = (eventId: string) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(eventId)) {
            newExpandedRows.delete(eventId);
        } else {
            newExpandedRows.add(eventId);
        }
        setExpandedRows(newExpandedRows);
    };

    const formatTimestamp = (unixTimestamp: number) => {
        // Convert Unix timestamp (seconds) to milliseconds and create Date object
        const date = new Date(unixTimestamp * 1000);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
        });
    };

    const truncateHash = (hash: string) => {
        return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
    };

    return (
        <div className="space-y-6">
            {/* Filters Section */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="p-4 sm:p-4">
                    <EventFilters dateRange={dateRange} eventNameInput={eventNameFilter} onDateRangeChange={setDateRange} onEventNameChange={setEventNameFilter} onCustomDateChange={handleCustomDateChange} onApplyFilters={handleApplyFilters} onClearFilters={handleClearFilters} showEventNameFilter={true} showStateFilter={true} stateId={stateId} onStateChange={handleStateChange} initialFromDate={customFromDate} initialToDate={customToDate} isLoading={isApplyingFilters} />
                </Card>
            </motion.div>

            {/* Events Table */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <Card className="overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <>
                            <EventTable events={events} expandedRows={expandedRows} onToggleExpand={toggleRowExpansion} formatTimestamp={formatTimestamp} truncateHash={truncateHash} />
                            <EventPagination currentPage={currentPage} totalPages={totalPages} totalEvents={totalEvents} itemsPerPage={itemsPerPage} onPageChange={goToPage} onItemsPerPageChange={handleItemsPerPageChange} />
                        </>
                    )}
                </Card>
            </motion.div>
        </div>
    );
};

export default Transactions;

